import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from '../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { useInvoices } from '../hooks/useApi';
import { formatCurrency, formatDate, getStatusBadgeClass, storage, todayYMD, addDaysYMD } from '../lib/utils';
import { toast } from 'sonner';
import { 
  Plus, 
  Search, 
  FileText, 
  MoreVertical, 
  Eye, 
  Download, 
  Trash2,
  CheckCircle,
  Send,
  XCircle,
  PenLine,
  Copy
} from 'lucide-react';

// Get draft from localStorage
const getDraft = () => {
  const draft = storage.get('invoiceflow_draft');
  if (draft && (draft.customer_name || draft.line_items?.some(item => item.description))) {
    // Calculate draft total
    const subtotal = draft.line_items?.reduce((sum, item) => 
      sum + (parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0), 0
    ) || 0;
    const taxAmount = subtotal * (parseFloat(draft.tax_rate) || 0) / 100;
    const total = subtotal + taxAmount;
    
    return {
      id: 'draft',
      invoice_number: 'DRAFT',
      customer_name: draft.customer_name || 'Untitled Draft',
      issue_date: draft.issue_date,
      due_date: draft.due_date,
      total: total,
      currency: draft.currency || 'USD',
      status: 'unsaved',
      isDraft: true
    };
  }
  return null;
};

const InvoicesPage = () => {
  const navigate = useNavigate();
  const { getInvoices, deleteInvoice, updateStatus, downloadPdf, loading } = useInvoices();
  
  const [invoices, setInvoices] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [deleteId, setDeleteId] = useState(null);
  const [localDraft, setLocalDraft] = useState(null);

  useEffect(() => {
    loadInvoices();
    setLocalDraft(getDraft());
  }, []);

  const loadInvoices = async () => {
    const { data } = await getInvoices();
    if (data) {
      setInvoices(data);
    }
  };

  useEffect(() => {
    let filtered = [...invoices];
    
    // Add local draft at the beginning if it exists
    const draft = getDraft();
    setLocalDraft(draft);
    
    if (draft && (statusFilter === 'all' || statusFilter === 'unsaved')) {
      // Check if draft matches search
      if (!search || 
          draft.customer_name.toLowerCase().includes(search.toLowerCase()) ||
          'draft'.includes(search.toLowerCase())) {
        filtered = [draft, ...filtered];
      }
    }
    
    if (search) {
      filtered = filtered.filter(inv => 
        inv.isDraft || // Keep draft if already added
        inv.customer_name.toLowerCase().includes(search.toLowerCase()) ||
        inv.invoice_number.toString().includes(search)
      );
    }
    
    if (statusFilter !== 'all' && statusFilter !== 'unsaved') {
      filtered = filtered.filter(inv => inv.isDraft || inv.status === statusFilter);
    } else if (statusFilter === 'unsaved') {
      filtered = filtered.filter(inv => inv.isDraft);
    }
    
    setFilteredInvoices(filtered);
  }, [search, statusFilter, invoices]);

  const handleDeleteDraft = () => {
    storage.remove('invoiceflow_draft');
    setLocalDraft(null);
    setFilteredInvoices(prev => prev.filter(inv => !inv.isDraft));
    toast.success('Draft deleted');
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    if (deleteId === 'draft') {
      handleDeleteDraft();
      setDeleteId(null);
      return;
    }
    const { error } = await deleteInvoice(deleteId);
    if (!error) {
      toast.success('Invoice deleted');
      loadInvoices();
    }
    setDeleteId(null);
  };

  const handleStatusChange = async (id, status) => {
    const { error } = await updateStatus(id, status);
    if (!error) {
      toast.success(`Invoice marked as ${status}`);
      loadInvoices();
    }
  };

  const handleDuplicate = async (invoiceId) => {
    // Fetch the full invoice data
    const { getInvoice } = await import('../hooks/useApi').then(m => ({ getInvoice: m.useInvoices().getInvoice }));
    
    // Get invoice from the list (we have basic data)
    const invoice = invoices.find(inv => inv.id === invoiceId);
    if (!invoice) {
      toast.error('Invoice not found');
      return;
    }

    // Fetch full invoice details via API
    const token = storage.get('token');
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/invoices/${invoiceId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const fullInvoice = await response.json();

      // Create draft from invoice data (excluding id, invoice_number, status, created_at)
      const draft = {
        customer_id: fullInvoice.customer_id || '',
        customer_name: fullInvoice.customer_name || '',
        customer_address: fullInvoice.customer_address || '',
        customer_city: fullInvoice.customer_city || '',
        customer_postal_code: fullInvoice.customer_postal_code || '',
        customer_country: fullInvoice.customer_country || '',
        customer_tax_id: fullInvoice.customer_tax_id || '',
        line_items: fullInvoice.line_items || [{ description: '', quantity: 1, unit_price: 0, unit: 'pcs' }],
        tax_rate: fullInvoice.tax_rate || 0,
        currency: fullInvoice.currency || 'USD',
        issue_date: fullInvoice.issue_date || todayYMD(),
        due_date: fullInvoice.due_date || addDaysYMD(todayYMD(), 30),
        footer_notes: fullInvoice.footer_notes || '',
        payment_conditions: fullInvoice.payment_conditions || ''
      };

      // Save to localStorage
      storage.set('invoiceflow_draft', draft);
      toast.success('Invoice copied to draft');
      navigate('/invoices/new');
    } catch (error) {
      toast.error('Failed to copy invoice');
    }
  };

  return (
    <div className="space-y-6" data-testid="invoices-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Invoices</h1>
          <p className="text-muted-foreground mt-1">Manage your invoices</p>
        </div>
        <Button 
          data-testid="new-invoice-btn"
          onClick={() => navigate('/invoices/new')}
          className="gap-2 bg-accent hover:bg-accent/90"
        >
          <Plus className="w-4 h-4" />
          New Invoice
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                data-testid="invoice-search"
                placeholder="Search by customer or invoice number..."
                className="pl-9"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40" data-testid="status-filter">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="unsaved">Unsaved Draft</SelectItem>
                <SelectItem value="created">Created</SelectItem>
                <SelectItem value="sent">Sent</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Invoices List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            {filteredInvoices.length} Invoice{filteredInvoices.length !== 1 ? 's' : ''}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="spinner" />
            </div>
          ) : filteredInvoices.length === 0 ? (
            <div className="empty-state">
              <FileText className="w-12 h-12 mb-4 opacity-50" />
              <p className="font-medium">No invoices found</p>
              <p className="text-sm">
                {search || statusFilter !== 'all' 
                  ? 'Try adjusting your filters' 
                  : 'Create your first invoice to get started'}
              </p>
              {!search && statusFilter === 'all' && (
                <Button 
                  className="mt-4 gap-2" 
                  onClick={() => navigate('/invoices/new')}
                >
                  <Plus className="w-4 h-4" />
                  Create Invoice
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Invoice #</th>
                    <th>Customer</th>
                    <th>Date</th>
                    <th>Due Date</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th className="w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredInvoices.map((invoice) => (
                    <tr 
                      key={invoice.id}
                      className={`cursor-pointer ${invoice.isDraft ? 'bg-amber-50/50' : ''}`}
                      onClick={() => invoice.isDraft ? navigate('/invoices/new') : navigate(`/invoices/${invoice.id}`)}
                      data-testid={`invoice-row-${invoice.invoice_number}`}
                    >
                      <td>
                        <span className={`font-mono font-semibold ${invoice.isDraft ? 'text-amber-600' : ''}`}>
                          {invoice.isDraft ? (
                            <span className="flex items-center gap-1">
                              <PenLine className="w-4 h-4" />
                              DRAFT
                            </span>
                          ) : (
                            `#${invoice.invoice_number}`
                          )}
                        </span>
                      </td>
                      <td>{invoice.customer_name}</td>
                      <td className="text-muted-foreground">
                        {formatDate(invoice.issue_date)}
                      </td>
                      <td className="text-muted-foreground">
                        {formatDate(invoice.due_date)}
                      </td>
                      <td>
                        <span className="font-mono font-semibold">
                          {formatCurrency(invoice.total, invoice.currency)}
                        </span>
                        {invoice.currency !== 'USD' && invoice.total_usd != null && (
                          <div className="font-mono text-xs text-muted-foreground">
                            {formatCurrency(invoice.total_usd, 'USD')}
                          </div>
                        )}
                      </td>
                      <td>
                        <Badge 
                          variant="outline"
                          className={invoice.isDraft ? 'bg-amber-100 text-amber-700 border-amber-200' : getStatusBadgeClass(invoice.status)}
                        >
                          {invoice.isDraft ? 'unsaved' : invoice.status}
                        </Badge>
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        {invoice.isDraft ? (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button 
                                variant="ghost" 
                                size="icon"
                                data-testid="draft-menu"
                              >
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem 
                                onClick={() => navigate('/invoices/new')}
                              >
                                <PenLine className="w-4 h-4 mr-2" />
                                Continue Editing
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem 
                                className="text-destructive"
                                onClick={() => setDeleteId('draft')}
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete Draft
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        ) : (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              data-testid={`invoice-menu-${invoice.invoice_number}`}
                            >
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem 
                              onClick={() => navigate(`/invoices/${invoice.id}`)}
                            >
                              <Eye className="w-4 h-4 mr-2" />
                              View
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => downloadPdf(invoice.id, invoice.invoice_number)}
                            >
                              <Download className="w-4 h-4 mr-2" />
                              Download PDF
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => handleDuplicate(invoice.id)}
                            >
                              <Copy className="w-4 h-4 mr-2" />
                              Duplicate
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            {invoice.status === 'created' && (
                              <DropdownMenuItem 
                                onClick={() => handleStatusChange(invoice.id, 'sent')}
                              >
                                <Send className="w-4 h-4 mr-2" />
                                Mark as Sent
                              </DropdownMenuItem>
                            )}
                            {invoice.status !== 'paid' && invoice.status !== 'cancelled' && (
                              <DropdownMenuItem 
                                onClick={() => handleStatusChange(invoice.id, 'paid')}
                              >
                                <CheckCircle className="w-4 h-4 mr-2" />
                                Mark as Paid
                              </DropdownMenuItem>
                            )}
                            {invoice.status !== 'cancelled' && (
                              <DropdownMenuItem 
                                onClick={() => handleStatusChange(invoice.id, 'cancelled')}
                              >
                                <XCircle className="w-4 h-4 mr-2" />
                                Cancel
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              className="text-destructive"
                              onClick={() => setDeleteId(invoice.id)}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {deleteId === 'draft' ? 'Delete Draft?' : 'Delete Invoice?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {deleteId === 'draft' 
                ? 'This will delete your unsaved draft. This action cannot be undone.'
                : 'This action cannot be undone. The invoice will be permanently deleted.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default InvoicesPage;
